import React from 'react';
import { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle } from 'react-icons/fi';
import SimplePasswordChangeModal from './SimplePasswordChangeModal';
import ForgotUsernameModal from './ForgotUsernameModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import OtpCodeInput from './OtpCodeInput';
import useSimpleLogin from './useSimpleLogin';
import {
  LoginContainer, Form, FormGroup, InputWrapper, Input, Icon, PasswordToggle, Button,
  EnrollNowButton, AlertBanner, AlertIcon, AlertText, GreetingWrapper, ForgotUsernameLink,
  HelpLink, LoadingSpinner, MethodToggle, MethodToggleButton, OtpHeading, OtpDescription,
  OtpInfoText, OtpResendText, OtpSwitchEmail
} from './SimpleLogin.styles';
// Note: SimpleLogin is rendered inside AuthContainer/AuthCard from App.js
// So we don't need AuthPage wrapper here, just render content directly

function SimpleLogin() {
  const {
    formData, showPassword, setShowPassword, loading, error,
    showPasswordChangeModal, setShowPasswordChangeModal,
    showForgotUsernameModal, setShowForgotUsernameModal,
    showForgotPasswordModal, setShowForgotPasswordModal,
    showSessionExpired, showLoggedOut, navigate,
    handleChange, handleSubmit, handlePasswordChangeSuccess, handleNeedHelp,
    authMethod, otpStep, otpCode, setOtpCode, otpRequestLoading, otpInfoMessage,
    resendCooldown, handleToggleAuthMethod, handleRequestOtp, handleResendOtp, handleVerifyOtp
  } = useSimpleLogin();

  const formatCooldown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <>
      <LoginContainer>
        <GreetingWrapper>
          <span className="arabic-text">السلام عليكم</span>
        </GreetingWrapper>

        <MethodToggle role="tablist">
          <MethodToggleButton
            type="button"
            role="tab"
            $active={authMethod === 'password'}
            aria-selected={authMethod === 'password'}
            onClick={() => handleToggleAuthMethod('password')}
          >
            Password
          </MethodToggleButton>
          <MethodToggleButton
            type="button"
            role="tab"
            $active={authMethod === 'otp'}
            aria-selected={authMethod === 'otp'}
            onClick={() => handleToggleAuthMethod('otp')}
          >
            One-time code
          </MethodToggleButton>
        </MethodToggle>

        {/* All error messages displayed above the active form */}
        {showLoggedOut && (
          <AlertBanner
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            role="alert"
          >
            <AlertIcon>
              <FiAlertCircle />
            </AlertIcon>
            <AlertText>
              You have been logged out. Please sign in again to continue.
            </AlertText>
          </AlertBanner>
        )}

        {showSessionExpired && (
          <AlertBanner
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            role="alert"
          >
            <AlertIcon>
              <FiAlertCircle />
            </AlertIcon>
            <AlertText>
              Your session has expired. Please sign in again to continue.
            </AlertText>
          </AlertBanner>
        )}

        {error && (
          <AlertBanner
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            role="alert"
          >
            <AlertIcon>
              <FiAlertCircle />
            </AlertIcon>
            <AlertText>
              {error}
            </AlertText>
          </AlertBanner>
        )}

        {authMethod === 'password' && (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiMail size={16} />
                </Icon>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiLock size={16} />
                </Icon>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggle>
              </InputWrapper>
            </FormGroup>

            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </Form>
        )}

        {authMethod === 'otp' && otpStep === 'request' && (
          <Form onSubmit={handleRequestOtp}>
            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiMail size={16} />
                </Icon>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <Button type="submit" disabled={otpRequestLoading}>
              {otpRequestLoading ? (
                <>
                  <LoadingSpinner />
                  Sending...
                </>
              ) : (
                'Send a code'
              )}
            </Button>
          </Form>
        )}

        {authMethod === 'otp' && otpStep === 'verify' && (
          <Form onSubmit={handleVerifyOtp}>
            <OtpHeading>Check your email</OtpHeading>
            <OtpDescription>
              We sent a 6-digit code to <strong>{formData.email}</strong>. It expires in 10 minutes.
            </OtpDescription>

            {otpInfoMessage && <OtpInfoText>{otpInfoMessage}</OtpInfoText>}

            <OtpCodeInput value={otpCode} onChange={setOtpCode} />

            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  Signing in...
                </>
              ) : (
                'Verify & sign in'
              )}
            </Button>

            <OtpResendText>
              {resendCooldown > 0 ? (
                `Resend code in ${formatCooldown(resendCooldown)}`
              ) : (
                <button type="button" onClick={handleResendOtp} disabled={otpRequestLoading}>
                  Resend code
                </button>
              )}
            </OtpResendText>

            <OtpSwitchEmail>
              <button type="button" onClick={() => handleToggleAuthMethod('otp')}>
                Use a different email
              </button>
            </OtpSwitchEmail>
          </Form>
        )}

        {/* New students browse the public catalog and create their account when they pay. */}
        <EnrollNowButton className="enroll-now-button" onClick={() => navigate('/catalog')}>
          Enroll Now
        </EnrollNowButton>

        <ForgotUsernameLink>
          <button onClick={() => setShowForgotUsernameModal(true)}>
            Forgot your email?
          </button>
          <span className="separator">|</span>
          <button onClick={() => setShowForgotPasswordModal(true)}>
            Forgot your password?
          </button>
        </ForgotUsernameLink>

        <HelpLink>
          <button onClick={handleNeedHelp}>
            Need help?
          </button>
        </HelpLink>
      </LoginContainer>

      <SimplePasswordChangeModal
        isOpen={showPasswordChangeModal}
        onClose={() => setShowPasswordChangeModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />

      <ForgotUsernameModal
        isOpen={showForgotUsernameModal}
        onClose={() => setShowForgotUsernameModal(false)}
      />

      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </>
  );
}

export default SimpleLogin;
